import { JL } from 'jsnlog';
import { PreJaabstracts } from './civil-case-sections/pre-jaabstracts';
import { Transcript } from './civil-case-sections/transcript';
import { Abstract } from './civil-case-sections/abstract';
import { Attorney } from './civil-case-sections/attorney';
import { Order } from './civil-case-sections/order';
import { ServiceOfDocuments } from './civil-case-sections/serviceOfDocuments';
import { CaseEventHistory } from './civil-case-sections/caseEventHistory';
import { ConsolidatedCase } from './civil-case-sections/consolidatedCase';
import { TransferAndAppeals } from './civil-case-sections/transferAndAppeals';
import { TransferAppealsComponent } from './../case-view/civil/transfer-appeals/transfer-appeals.component';
import { Microfilm } from './civil-case-sections/microfilm';
import { Party } from './civil-case-sections/party';
import { Issue } from './civil-case-sections/issue';
import { SafeJSON } from './../shared/safe-json';
import { FileNumberUtil } from './../shared/file-number-util';
import { CaseIdentifier } from './../shared/case-identifier';
import { Pleading } from './civil-case-sections/pleading';
import { DatePipe } from '@angular/common';
import { environment } from 'environments/environment.fields';

const enum objectType {
    Issue,
    Party,
    Pleading,
    Microfilm,
    TransferedFromCase,
    TransferAndAppeals,
    ConsolidatedCase,
    CaseEventHistory,
    ServiceOfDocuments,
    Order,
    Attorney,
    Abstract,
    PreJAAbstract,
    Transcript
};

export class CivilCase {
    json: any;
    caseIdentifier: CaseIdentifier;
    private partyMap: Map<string, Party>;
    private pleadingMap: Map<string, Pleading>;
    private issueMap: Map<string, Issue>;
    private microfilmMap: Map<string, Microfilm>;
    private appealedIssueMap: Map<string, TransferAndAppeals>;
    private consolidatedCaseMap: Map<string, ConsolidatedCase>;
    private caseEventHistoryMap: Map<string, CaseEventHistory>;
    private serviceOfDocumentsMap: Map<string, ServiceOfDocuments>;
    private orderMap: Map<string, Order>;
    private attorneyMap: Map<string, Attorney>;
    private abstractMap: Map<string, Abstract>;
    private preJAAbstractMap: Map<string, PreJaabstracts>;
    private transcriptMap: Map<string, Transcript>;
    searchRecordLastUpdateDate: string = new Date().toJSON();

    public static logToConsoleAndServer(text: string) {
        JL().error(text);
        console.log(text);
    }
    constructor(json: any) {
        this.caseIdentifier =
            new CaseIdentifier('CIVIL',
                json.civilCase.caseNumber.county,
                json.civilCase.caseNumber.fileNumber);
        this.json = json;

        this.issueMap = this.buildMap<Issue>(SafeJSON.get('issues', json), objectType.Issue);
        this.partyMap = this.buildMap<Party>(SafeJSON.get('caseParties', json), objectType.Party);
        this.pleadingMap = this.buildMap<Pleading>(SafeJSON.get('pleadings', json), objectType.Pleading);
        this.microfilmMap = this.buildMap<Microfilm>(SafeJSON.get('microfilmList', json), objectType.Microfilm);
        this.appealedIssueMap = this.buildMap<TransferAndAppeals>(SafeJSON.get('appealsList', json), objectType.TransferAndAppeals);
        this.consolidatedCaseMap = this.buildMap<ConsolidatedCase>(SafeJSON.get('consolidatedCaseList', json), objectType.ConsolidatedCase);
        this.caseEventHistoryMap = this.buildMap<CaseEventHistory>(SafeJSON.get('caseEventList', json), objectType.CaseEventHistory);
        this.serviceOfDocumentsMap = this.buildMap<ServiceOfDocuments>(SafeJSON.get('srvDocsList', json), objectType.ServiceOfDocuments);
        this.orderMap = this.buildMap<Order>(SafeJSON.get('orders', json), objectType.Order);
        this.attorneyMap = this.buildMap<Attorney>(SafeJSON.get('attorneyList', json), objectType.Attorney);
        this.abstractMap = this.buildMap<Abstract>(SafeJSON.get('abstracts', json), objectType.Abstract);
        this.preJAAbstractMap = this.buildMap<PreJaabstracts>(SafeJSON.get('preJAAbstracts', json), objectType.PreJAAbstract);
        this.transcriptMap = this.buildMap<Transcript>(SafeJSON.get('transcriptList', json), objectType.Transcript);
    }

    private buildMap<T>(obj, type: objectType): Map<string, T> {
        let map = new Map();
        Object.keys(obj).forEach(key => {
            let value;
            switch (type) {
                case objectType.Issue:
                    value = new Issue(obj[key], key);
                    break;
                case objectType.Party:
                    // Since new json structure gives us two lists, one for each party type (D, P, etc)
                    // and a list of parties, in order, we need to combine the two lists back into
                    // one list for each type.  For exampl, enstead of having D: {1,2,3,...} and P: (1,2,...}
                    // we now have {D1, D2, D3, ...} and {P1, P2, P3,...}.
                    const partiesByRole = obj[key];
                    Object.keys(partiesByRole).forEach(partyNumber => {
                        // Get each party from the list for that party type (D, P, etc)
                        const partyKey = key + partyNumber;
                        let partyValue = new Party(partiesByRole[partyNumber], partyKey);
                        map.set(partyKey, partyValue);
                    });
                    return map; // we need to return here since we already added the mappings
                case objectType.Pleading:
                    value = new Pleading(obj[key], key);
                    break;
                case objectType.Microfilm:
                    value = new Microfilm(obj[key], key);
                    break;
                case objectType.TransferAndAppeals:
                    value = new TransferAndAppeals(obj[key], key);
                    break;
                case objectType.ConsolidatedCase:
                    value = new ConsolidatedCase(obj[key], key);
                    break;
                case objectType.CaseEventHistory:
                    value = new CaseEventHistory(obj[key], key);
                    break;
                case objectType.ServiceOfDocuments:
                    value = new ServiceOfDocuments(obj[key], key);
                    break;
                case objectType.Order:
                    value = new Order(obj[key], key);
                    break;
                case objectType.Attorney:
                    value = new Attorney(obj[key], key);
                    break;
                case objectType.Abstract:
                    value = new Abstract(obj[key], key);
                    break;
                case objectType.PreJAAbstract:
                    value = new PreJaabstracts(obj[key], key);
                    break;
                case objectType.Transcript:
                    value = new Transcript(obj[key], key);
                    break;
                default:
                    break;
            }
            map.set(key, value);
        });
        return map;
    }
    getpartyMap(): Map<string, Party> {
       return this.partyMap;
    }
    getCaseIdentifier(): CaseIdentifier {
        return this.caseIdentifier;
    }
    /*
    UX319	Based on the case type, parties will be sorted by party role in the specified order and then by party sequence number in
    ascending numerical order within each party role. Party roles will be sorted as follows:
        UX319.1	CVM, CVD, and CVS case types: PLAINTIFF/PETITIONER, DEFENDANT, and INTERESTED PARTY.
        UX319.2	CR, CRS, and J case types: PLAINTIFF/PETITIONER, DEFENDANT, FIDUCIARY, and SURETY.
        UX319.3	E and EO case types: DECEDENT/WARD/TRUSTEE, FIDUCIARY, and BENEFICIARY/DEVISEE.
        UX319.4	SP case type: PETITIONER, FIDUCIARY, RESPONDENT, DECEDENT/WARD/TRUSTEE, and SURETY.
        UX319.5	R case type: PETITIONER, and RESPONDENT.
        UX319.6	M case type: PLAINTIFF/PETITIONER, DEFENDANT, DECEDENT/WARD/TRUSTEE, FIDUCIARY, and SURETY .
    */
    // this sorting must match what's in abstract.getAbstractPartyLiableList()
    getPartyList(): Party[] {


        let partyList: Party[] = [];
        let caseType = this.getCaseType();
        if (caseType === 'CR' || caseType === 'CRS' || caseType === 'J') {
            return partyList
            .concat( this.getPartyListByRoleCode('P'))
            .concat( this.getPartyListByRoleCode('D'))
            .concat( this.getPartyListByRoleCode('F'))
            .concat( this.getPartyListByRoleCode('S'));
        }
        if (caseType === 'E' || caseType === 'EO') {
            return partyList
            .concat( this.getPartyListByRoleCode('E'))
            .concat( this.getPartyListByRoleCode('F'))
            .concat( this.getPartyListByRoleCode('B'));
        }
        if (caseType === 'SP') {
            return partyList
            .concat( this.getPartyListByRoleCode('P'))
            .concat( this.getPartyListByRoleCode('F'))
            .concat( this.getPartyListByRoleCode('R'))
            .concat( this.getPartyListByRoleCode('E'))
            .concat( this.getPartyListByRoleCode('S'));
        }
        if (caseType === 'R') {
            return partyList
            .concat( this.getPartyListByRoleCode('P'))
            .concat( this.getPartyListByRoleCode('R'));
        }
        if (caseType === 'M') {
            return partyList
            .concat( this.getPartyListByRoleCode('P'))
            .concat( this.getPartyListByRoleCode('D'))
            .concat( this.getPartyListByRoleCode('E'))
            .concat( this.getPartyListByRoleCode('F'))
            .concat( this.getPartyListByRoleCode('S'));
        }
        if (caseType !== 'CVM' && caseType !== 'CVD' && caseType !== 'CVS') {
            // getting here would be an error condition.
            console.log('getPartyList: caseType=' + caseType +
                        ', error - case type not recognized! Using the party list we received from WS.');
        }
        // by default use the CVM ordering
        return partyList
            .concat( this.getPartyListByRoleCode('P'))
            .concat( this.getPartyListByRoleCode('D'))
            .concat( this.getPartyListByRoleCode('I'));
    }
    /*
        UX319	Based on the case type, parties will be sorted by party role in the specified order and then by party sequence number in
        ascending numerical order within each party role. Party roles will be sorted as follows:
    */
    getGroupedAndSortedPartyList(partyKeyList: any[]): any[] {
        let caseType = this.getCaseType();
        let partyList: Party[] = [];
        if (caseType === 'CR' || caseType === 'CRS' || caseType === 'J') {
            return partyList
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'P'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'D'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'F'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'S'))
        }
        if (caseType === 'E' || caseType === 'EO') {
            return partyList
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'E'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'F'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'B'))
        }
        if (caseType === 'SP') {
            return partyList
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'P'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'F'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'R'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'D'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'S'))
        }
        if (caseType === 'R') {
            return partyList
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'P'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'R'))
        }
        if (caseType === 'M') {
            return partyList
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'P'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'D'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'E'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'R'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'S'))
        }
        if (caseType !== 'CVM' && caseType !== 'CVD' && caseType !== 'CVS') {
            // getting here would be an error condition.
            console.log('getPartyList: caseType=' + caseType +
                        ', error - case type not recognized! Using the party list we received from WS.');
        }
        // by default use the CVM ordering
        return partyList
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'P'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'D'))
            .concat( this.getPartyListByRoleCodeFromList(partyKeyList, 'I'))
    }
    getPartyListByIssueKey(issueKey: string): Party[] {
        const issue = this.issueMap.get(issueKey);
        const partyKeys: string[] = issue.getIssuePartyCodes();

        const result = [];
        // First get the By parties
        for (let partyKey of partyKeys) {

            // we may get a party key that doesn't exist in the caseParties list
            if (! this.partyMap.has(partyKey)) {
                continue;
            }

            let party = this.partyMap.get(partyKey);
            party.setPartyKey(partyKey);   // change key from '1' to 'D1' for example

            if (issue.getIssueOrderPartyRole(party) === 'BY') {
                result.push(party);
            }
        }
        let byList = this.getGroupedAndSortedPartyList(result);

        // Then add the Against parties
        const resultAgainst = [];
        for (let partyKey of partyKeys) {

            // we may get a party key that doesn't exist in the caseParties list
            if (! this.partyMap.has(partyKey)) {
                continue;
            }

            let party = this.partyMap.get(partyKey);
            party.setPartyKey(partyKey);   // change key from '1' to 'D1' for example

            if (issue.getIssueOrderPartyRole(party) === 'AGAINST') {
                resultAgainst.push(party);
            }
        }
        let againstList = this.getGroupedAndSortedPartyList(resultAgainst);

        // lastly combine the grouped and sorted list and return it
        return byList.concat(againstList);
    }
    getIssueClockedInDate(civilCase: CivilCase, pleadingKey: string): string {
        const pleading: Pleading = civilCase.getPleadingByKey(pleadingKey);
        return pleading.getClockDate();
    }
    getIssueType(civilCase: CivilCase, issue: Issue): string {
        const issueType = issue.getType();
        return issueType;
    }
    getPleadingTypeDescription(civilCase: CivilCase, pleadingKey: string): string {
        const pleading: Pleading = civilCase.getPleadingByKey(pleadingKey);
        const description = pleading.getPleadingTypeDescription();
        return description;
    }
    getPartyByKey(partyKey: string): Party {
        return this.partyMap.get(partyKey);
    }
    getAbstractByKey(abstractRefKey: string): Abstract {
        return this.abstractMap.get(abstractRefKey);
    }
    getPreJAAbstractByKey(abstractRefKey: string): PreJaabstracts {
        return this.preJAAbstractMap.get(abstractRefKey);
    }
    getAbstractList(code: string): Abstract[] {
        return Array.from(this.abstractMap.values())
            .filter(abstract => abstract.getAbstractCode() === code);
    }
    getPreJAAbstractList(): PreJaabstracts[] {
        let list = Array.from(this.preJAAbstractMap.values());
        list.sort( function(p1, p2) {
            return p1.getAbstractJudgmentNumber().localeCompare(p2.getAbstractJudgmentNumber());
        });
        return list;
    }
    getAbstractPrePostList(code: string): (Abstract | PreJaabstracts)[] {
        const preJAList = this.getPreJAAbstractList();
        const prePostList = Array.from(this.abstractMap.values())
                                    .filter(abstract => abstract.getAbstractCode() === code);
        return [...preJAList, ...prePostList];
    }
    /*
        B = BENEFICIARY / DEVISEE
        D = DEFENDANT
        E = ESTATE
        F = FIDUCIARY
        I = INTERESTED PARTY
        P = PLAINTIFF / PETITIONER
        R = RESPONDENT
        S = SURETY
    */
    getPartyListByRoleCode(roleCode: string): Party[] {
        return Array.from(this.partyMap.values())
            .filter(party => party.getPartyRoleCode() === roleCode);
    }
    getPartyListByRoleCodeFromList(partyList: any[], roleCode: string): Party[] {
        let partyRoleList = partyList.filter(party => party.getPartyRoleCode() === roleCode);

        partyRoleList
            .sort( function(p1, p2) {
                let role1: string = p1.key;
                role1 = role1.substring(1, role1.length);
                const role1Number = Number.parseInt(role1);

                let role2: string = p2.key;
                role2 = role2.substring(1, role2.length);
                const role2Number = Number.parseInt(role2);
                return role1Number - role2Number;
            })
        ;
        return partyRoleList;
    }
    getSpecialAbstractList(): Abstract[] {
        return Array.from(this.abstractMap.values())
            .filter(abstract => abstract.getAbstractCode() === 'Q');
    }
    getIssueListByAbstractKey(abstractRefKey: string): Issue[] {
        /*
        FBR430	Display a listing of the issues covered by this Judgment
        sorted on the Issue Clocked In Date in chronological order first and
        then by the Pleading Type and the Issue Type in alphabetical order.
        */
        let list: Issue[] = Array.from(this.issueMap.values())
            .filter(issue => issue.getAbstractRefKey() === abstractRefKey);

        list
            .sort((p1, p2) => {
                let compare = p1.getPleadingClockedInDate(this).localeCompare(p2.getPleadingClockedInDate(this));
                if (compare !== 0) {
                    return compare;
                }
                compare = p1.getPleadingDescription(this).localeCompare(p2.getPleadingDescription(this));
                if (compare !== 0) {
                    return compare;
                }
                return p1.getType().localeCompare(p2.getType());
            });
        return list;
    }

    /**
     *  This method overides the one above: getIssueListByAbstractKey(abstractRefKey: string), 
     *  for resolving the CQ: TSD00037424
     * @param abstract Abstract
     */
    getIssueDisplayListByAbstract(abstract: Abstract): any[] {
        /*
        FBR430	Display a listing of the issues covered by this Judgment
        sorted on the Issue Clocked In Date in chronological order first and
        then by the Pleading Type and the Issue Type in alphabetical order.
        */
        let list: Issue[] = Array.from(this.issueMap.values())
            .filter(issue => issue.getAbstractRefKey() === abstract.getKey());

        let issueDisplayListByAbstractJudgment: any[] = [];
        for (let issue of list) {
            let issueClockedInDate: any = abstract.getIssueClockedInDate(this, issue.getPleadingKey());
            let issuePleading: string = abstract.getPleadingTypeDescription(this, issue.getPleadingKey());
            let issueType: string = abstract.getIssueType(this, issue).trim();

            let issueRow: any = {
                issueClockedInDate: issueClockedInDate.trim(),
                issuePleading: issuePleading.trim(),
                issueType: issueType.trim()
             };
             issueDisplayListByAbstractJudgment.push(issueRow);
         }

        // sorting in issue clock date, pleading  and issue type
        issueDisplayListByAbstractJudgment.sort( function(p1, p2) {
          if (p1.issueClockedInDate.localeCompare(p2.issueClockedInDate) === 0) {
            if (p1.issuePleading.localeCompare(p2.issuePleading)  === 0 ) {
               return p1.issueType.localeCompare(p2.issueType);
            } else {
               return p1.issuePleading.localeCompare(p2.issuePleading);
            }
          } else {
            return p1.issueClockedInDate.localeCompare(p2.issueClockedInDate);
          }
        });

        return issueDisplayListByAbstractJudgment;
    }

    getPleadingList(): Pleading[] {
         // Fixed sort to nesting verse chaining.
        const pleadingList = Array.from(this.pleadingMap.values());

        // sort pleadings list by clock date, then by pleading type (name)
        pleadingList.sort( function(p1, p2) {
            let result = p1.getClockDate().localeCompare(p2.getClockDate());
            if (result !== 0) {
                return result;
            }
            return p1.getType().localeCompare(p2.getType());
        });
        return pleadingList;
    }
    getPleadingByKey(key: string): Pleading {
        return this.pleadingMap.get(key);
    }
    getIssueListByKey(key: string): Issue {
        return this.issueMap.get(key);
    }
    // Buncombe 2004CVD5107 (has issues with OTHER descriptions which are now sorted correctly
    getIssueListByPleadingKey(pleadingKey: string): Issue[] {
        let issueList: Issue[] = Array.from(this.issueMap.values())
            .filter(issue => issue.getPleadingKey() === pleadingKey);

        // sorting by issue type, then issue id
        issueList.sort( function(p1, p2) {
            if (p1.json.issueId === undefined || p2.json.issueId === undefined) {
                return 0;
            }
            if (p1.getTypeReplaceOther().localeCompare(p2.getTypeReplaceOther()) === 0) {
                return p1.json.issueId.localeCompare(p2.json.issueId);
            } else {
                return p1.getTypeReplaceOther().localeCompare(p2.getTypeReplaceOther());
            }
        });
        return issueList;
    }
    getPartyNameByPartyNumber(partyNumber: string): string {
        for (let party of Array.from(this.partyMap.values())) {
            let partyNum = Number(partyNumber);
            if (party.getPartyNumber() === partyNum) {
                return party.getName();
            }
        }
        return '-';
    }
    getPartyNameByPartyRef(partyRef: string): string {
        let party = this.partyMap.get(partyRef);
        if (party === undefined) {
            return '-';
        }
        return party.getName();
    }
    getPartyDescriptionByPartyRef(partyRef: string): string {
        let party = this.partyMap.get(partyRef);
        if (party === undefined) {
            return '-';
        }
        return party.getPartyRole();
    }
    getPartyNumberByPartyRef(partyRef: string): number {
        let party = this.partyMap.get(partyRef);
        if (party === undefined) {
            return 0;
        }
        return party.getPartyNumber();
    }
    getPartyNamesByPartyRefList(partyRefList: any[] ) {
        let sortedPartyNames: string[] = [];
        for (let partyRef of partyRefList) {
            let partyName: string = this.getPartyNameByPartyRef(partyRef);
            sortedPartyNames.push(partyName);
        }
        if (sortedPartyNames.length !== 0) {
            sortedPartyNames.sort(function(p1, p2) {
            return p1.localeCompare(p2);
          });
        }
        return sortedPartyNames;
      }

    // Buncombe 04CVD5107
    getMicroFilmList(): Microfilm[] {
        return Array.from(this.microfilmMap.values());
    }
    /* Anson -- 2003CRS903
        Sorting is done as per the requirement. UI is showing wrong abstract
        information for the transcript that was transcriptedOn: 2003-09-11T10:20:18.586536.
        This transcript is not electronically abstracted and has only Book
        and Page Number (888) and so UI is supposed to "See clerk for details".
     */
    getTranscriptList(): Transcript[] {
        /*  Note David Finley helped with this sort.  Its not chained, its nested which is what I want.
        UX374 Display a listing of all the transcripts of judgments for the case sorted
                by the Date and Time of Transcript in chronological order and then
                 by the Abstract field
                    where the abstracts with a book and page number
                    will be listed first in ascending numerical order,
                    followed
                    by the abstracts with an alpha sequence number in alphabetical order and
                    then the abstracts with the numeric sequence numbers displayed in ascending numerical order.

        FBR492	Do not display book and page numbers ‘999 999’ and ‘888 888’ in the ‘Abstract’ field,
                instead display the corresponding electronic abstract number assigned for the abstract in
                VCAP or CIPRS. If the abstract number is not available, display “See clerk for details.”
        */
        let tlist: Transcript[] = Array.from(this.transcriptMap.values());
        if (!environment.production) {
           // console.log(tlist);
        }
        const SPECIALBOOKPAGE8S = '888';
        const SPECIALBOOKPAGE9S = '999';

        return tlist.sort( function(p1, p2) {
            if (p1 === undefined || p1 === null || p2 === undefined || p2 === null) {
                return 0;
            }
            let p1DateTime = p1.getDateTimeOmitSeconds();
            let p2DateTime = p2.getDateTimeOmitSeconds();
            if (p1DateTime === undefined || p1DateTime === null || p2DateTime === undefined || p2DateTime === null) {
                return 0;
            }
            const result = p1DateTime.localeCompare(p2DateTime);
            if (result !== 0) {
                return result;
            }

            // Dates are the same so look at the abstract number or the book/page
            if (result === 0) {
                const p1DocketBookNum = SafeJSON.get('docketBookNum', p1.json);
                const p1DocketPageNum = SafeJSON.get('docketPageNum', p1.json);
                const p2DocketBookNum = SafeJSON.get('docketBookNum', p2.json);
                const p2DocketPageNum = SafeJSON.get('docketPageNum', p2.json);

                if (p1DocketBookNum !== '' && p2DocketBookNum !== ''
                    && p1DocketBookNum !== SPECIALBOOKPAGE8S && p1DocketBookNum !== SPECIALBOOKPAGE9S
                    && p2DocketBookNum !== SPECIALBOOKPAGE8S && p2DocketBookNum !== SPECIALBOOKPAGE9S) {

                    if (p1DocketBookNum !== p2DocketBookNum) {
                        return p1.getDocketBookNum().localeCompare(p2.getDocketBookNum());
                    }
                    return p1.getDocketPageNum().localeCompare(p2.getDocketPageNum());
                }
            }
            // No book/page to compare, so just use the abstract number
            // Buncombe – 2008CR900011
            if (p1.json.abstractCode === undefined || p2.json.abstractCode === undefined) {
                return 0;
            }
            const abstractCodeCompare = p1.json.abstractCode.localeCompare(p2.json.abstractCode);
            if (abstractCodeCompare !== 0) {
                return abstractCodeCompare;
            }
            return p1.json.abstractNumber.localeCompare(p2.json.abstractNumber);
        });
    }
    getAppealedIssueList(): TransferAndAppeals[] {
        if (Array.from(this.appealedIssueMap.values()).length === 0) {
            return new Array<TransferAndAppeals>();
        }

        let list: any[] = Array.from(this.appealedIssueMap.values());
        // sort pleadings list by clock date, then by pleading type (name)
        list.sort( function(p1, p2) {
            if (p1.json.appealDate === undefined || p2.json.appealDate === undefined) {
                return 0;
            }
            return p1.json.appealDate.localeCompare(p2.json.appealDate);
        });
        return list;
    }
    // Camden county -- 1992CVM53
    getAppealToCase(): any {
        let section = SafeJSON.getWithDefault('appealTo', this.json);
        return section;
    }
    getAppealFromCase(): any {
        let section = SafeJSON.getWithDefault('appealFrom', this.json);
        return section;
    }
    getAppealToCaseNumber(): any {
        let caseNumber = SafeJSON.getWithDefault('appealTo.caseNumber.formattedShortFileNumber', this.json);
        caseNumber = FileNumberUtil.stripMiddleZeros(caseNumber);
        return this.getCourtTypeName(caseNumber) + ' ' + caseNumber;
    }
    getAppealFromCaseNumber(): any {
        let section = SafeJSON.getWithDefault('appealFrom.caseNumber.formattedShortFileNumber', this.json);
        section = FileNumberUtil.stripMiddleZeros(section);
        return section;
    }
    getAppealToAppealDate(): any {
        return SafeJSON.get('appealTo.appealDate', this.json);
    }
    getAppealFromAppealDate(): any {
        return SafeJSON.get('appealFrom.appealDate', this.json);
    }
    // DURHAM - 2016CVM2651
    getPerfectedIndication(appealSource: string): string {
        const fileCaseNumber: string = this.getCaseNumber();
        let caseType = FileNumberUtil.getCaseType(fileCaseNumber);
       // caseType = 'CVM'; // TODO remove
        if (caseType !== 'CVM') {
            return '';
        }
        let paidIndicator = this.getAppealFromPaidIndicator();
        if (appealSource === 'TO') {
            paidIndicator = this.getAppealToPaidIndicator();
        }
        if (paidIndicator === 'N') {
            return '(NOTICE OF APPEAL)';
        }
        return '';
    }
    getAppealFromPaidIndicator(): string {
        return SafeJSON.get('appealFrom.paidInd', this.json);
    }
    getAppealToPaidIndicator(): string {
        return SafeJSON.get('appealTo.paidInd', this.json);
    }
    getConsolidatedCaseList(): any[] {
        if (Array.from(this.consolidatedCaseMap.values()).length === 0) {
            return [];
        }
        // return a properly sorted list of consolidated cases

        // convert the map to an array so we can manipulate it easily
        let consolidatedCaseList: ConsolidatedCase[] = Array.from(this.consolidatedCaseMap.values());

        /* Sort:
             Consolidation Order Clock Date in chronological order,
             and then by
               Case County in alphabetical order,
                 and then by
                   Case Number in ascending order.

          Sort them in reverse (nested) order to build the array in the sort order we want!

          The nested sort comes in to play when the current sort callback function determines that
          the items are equal; then it moves down the chain to the next sort callback.
          For example, the dates are the same so look at the county names for those two items.
          Likewise, if the dates and county names are the same, look at the filecase numbers to
          determine the order.
        */
        consolidatedCaseList.sort( function(p1, p2) {
                let compare = p1.getConsolidationOrderClockDate().localeCompare(p2.getConsolidationOrderClockDate());
                if (compare !== 0) {
                    return compare;
                }
                compare = p1.getConsolidationCaseCounty().localeCompare(p2.getConsolidationCaseCounty());
                if (compare !== 0) {
                    return compare;
                }
                return p1.getConsolidationCaseNumber().localeCompare(p2.getConsolidationCaseNumber());
            });

        let leadCase = SafeJSON.get('leadConsolidatedCase', this.json);
        if (leadCase !== undefined && leadCase !== null) {
            // combine arrays with lead case on top
            let leadCaseList: ConsolidatedCase[] = new Array(new ConsolidatedCase(leadCase, ''));
            return leadCaseList.concat(consolidatedCaseList);
        }
        // we didn't find any lead case so just return the sorted list of consolidated cases
        return consolidatedCaseList;
    }
    getCaseEventHistoryList(): CaseEventHistory[] {
        return Array.from(this.caseEventHistoryMap.values());
    };
    getServiceOfDocumentsList(): ServiceOfDocuments[] {
        /*
            Display a listing of all the service records for the parties on the case,
            sorted by the Issued Date and Time in chronological order
            and then by Party To Be Served in alphabetical order.
        */
        let civilCasex = this;   // Note: you can't simply pass 'this' in the sort function
        let serviceOfDocuments: ServiceOfDocuments[] = Array.from(this.serviceOfDocumentsMap.values());
        if (serviceOfDocuments.length === 0) {
            return [];
        }
        serviceOfDocuments.sort((p1, p2) => {
            let compare = p1.getIssuedOnDate().localeCompare(p2.getIssuedOnDate());
            if (compare !== 0) {
                return compare;
            }
            return p1.getPartyNameByPartyRef(this).localeCompare(p2.getPartyNameByPartyRef(this));
        });
        return serviceOfDocuments;
    }
    getOrderList(): Order[] {
        return Array.from(this.orderMap.values());
    }
    getAttorneyList(): any[] {
        if (Array.from(this.attorneyMap.values()).length === 0) {
            return [];
        }
        const list: any[] = Array.from(this.attorneyMap.values());

        list.sort(function(p1, p2) {
            return p1.json.name.localeCompare(p2.json.name);
        });
        return list;
    }
    getCaseType(): string {
       return FileNumberUtil.getCaseType(this.getCaseNumber());
    }
    getCaseTypeCaptionDescription(): string {
        const description = SafeJSON.getWithDefault('caseTypeDescription', this.json);
        if (description !== '-') {
            return '(' + description + ')';
        }
        return '';
     }
    getCaseTypeDescription(): string {
      let caseType: string = this.getCaseType();
      return FileNumberUtil.getCaseTypeDescription(caseType);
    }
    getCourtTypeName(caseNumber): string {
      let caseType: string =  FileNumberUtil.getCaseType(caseNumber);
      return FileNumberUtil.getLongCourtTypeDescription(caseType);
    }
    getTransferAndAppealsNotice(): string {
        const transferTo = this.getTransferedTo();
        const appealTo = this.getAppealToCase();
        const appealsList: any[] = this.getAppealedIssueList();
        if (transferTo !== '-' || appealTo !== '-' || appealsList.length > 0) {
            return '(See Transfers and Appeals)';
        }
        return '-';
    }
    getFromAppealedCaseNumber(): string {
      // look for From case first
      let fileNumber: string =  FileNumberUtil.stripMiddleZeros(this.getInitiatedAppealFromCaseNumber());
      if (fileNumber !== '-') {
          let caseType: string =  FileNumberUtil.getCaseType(fileNumber);
          return '(appealed from ' + FileNumberUtil.getLongCourtTypeDescription(caseType) + ' ' + fileNumber + ')';
      }
      return '-';
    }
    getToAppealedCaseNumber(): string {
      // look for To case
      let fileNumber = FileNumberUtil.stripMiddleZeros(this.getAppealToFormattedShortFileNumber());
      if (fileNumber !== '') {
          let caseType: string =  FileNumberUtil.getCaseType(fileNumber);
          return '(appealed to ' + FileNumberUtil.getLongCourtTypeDescription(caseType) + ' ' + fileNumber + ')';
      }
      return '-';
    }
    getCaseStatus(): string {
        return SafeJSON.getWithDefault('caseStatus.code', this.json);
    }
    getCaseStatusDescription(): string {
        return SafeJSON.getWithDefault('caseStatus.description', this.json);
    }
    getJudgmentDocketedAndIndexed(): string {
        return SafeJSON.getWithDefault('judgmentDocketedAndIndexed', this.json);
    }
    getInitiatingPleadingAndIssue(): string {
        return SafeJSON.getWithDefault('initiatingPleadingAndIssue', this.json);
    }
    getCaseNumber(): string {
        let caseNumber: string = SafeJSON.get('civilCase.caseNumber.fileNumber', this.json);
        return FileNumberUtil.stripMiddleZeros(caseNumber);
    }
    getCaseCounty(): string {
        let countyName = SafeJSON.get('civilCase.caseNumber.county', this.json);
        if (countyName === '') {
            return SafeJSON.get('civilCase.caseNumber.countyNumber', this.json);
        }
        return countyName;
    }
    getCaseCaption(): string {
        return SafeJSON.get('caseCaption', this.json);
    }
    getPartyName(): string {
        return SafeJSON.getWithDefault('name', this.json);
    }

    // Case Information ///////////////////////////////////////////////////////
    getPresidingOfficialNumber(): string {
        return SafeJSON.getWithDefault('civilCase.presidingOfficialNumber', this.json);
    }
    getDomesticCaseType(): string {
        let caseType = this.getCaseType();
        if (caseType !== 'CVD') {
            return '-';
        }
        return SafeJSON.getWithDefault('civilCase.domesticType.description', this.json);
    }
    getDisposedOn(): string {
        let rawDate: string = SafeJSON.get('civilCase.dispositionClockDate', this.json);
        return rawDate;
    }
    getConsolidationOrderClockDate(consolidatedCase: any): string {
        return SafeJSON.get('dispositionClockDate', consolidatedCase.json);
    }
    getConsolidationCounty(consolidatedCase: any): string {
        return SafeJSON.getWithDefault('caseNumber.county', consolidatedCase.json);
    }
    getConsolidationCaseNumber(consolidatedCase: any): string {
        let consolidatedCaseNumber = SafeJSON.get('caseNumber.fileNumber', consolidatedCase.json);
        return consolidatedCaseNumber;
    }
    getConsolidationCaseCaption(consolidatedCase: any): string {
        return SafeJSON.getWithDefault('caseCaption', consolidatedCase.json);
    }

    /*
        UX323-CL Display the lead case information first and display the remaining case
        numbers sorted by the Consolidation Order Clock Date in chronological order and
        then by the Case County in alphabetical order and the by the Case Number in
        ascending order.

        NEW WS change: Code updated and JSON now will have consolidatedCaseList and
        leadConsolidatedCase properties on both Consolidated and Lead Cases too.
    */
    getConsolidatedCaseLeadIndicator(consolidatedCase: any): string {
        const LEADCASELABEL = '(Lead Case)';

        let leadConsolidatedCase = SafeJSON.get('leadConsolidatedCase', this.json);
        let leadConsolidatedCaseFileNumber = SafeJSON.get('caseNumber.fileNumber', leadConsolidatedCase);
        leadConsolidatedCaseFileNumber = FileNumberUtil.normalizeCaseNumber(leadConsolidatedCaseFileNumber);

        // DURHAM 06CVD4116
        // Camden 1996CVD100756
        // Is this case the lead case?
        // Match the lead case number to the case number in the list
        let consolidatedCaseFileNumber = this.getConsolidationCaseNumber(consolidatedCase);
        consolidatedCaseFileNumber = FileNumberUtil.normalizeCaseNumber(consolidatedCaseFileNumber);
        if (consolidatedCaseFileNumber === leadConsolidatedCaseFileNumber) {
            return LEADCASELABEL;
        }
        return '';
    }
    getPresidingOfficialName(): string {
        return SafeJSON.getWithDefault('civilCase.presidingOfficialName', this.json);
    }
    showJuryTrialWasRequestedField(): boolean {
        const caseType = this.getCaseType();
        if (caseType === 'CVM' || caseType === 'CR' || caseType === 'CRS' || caseType === 'R' || caseType === 'M') {
            return false;
        }
        return true;
    }
    getJuryTrialWasRequested(): string {
        let field = this.caseEventCodeFound('JRDM');
        if (field === '-') {
            return 'NO';
        }
        return field;
    }
    getEligibleforMediatedSettlementConference(): string {
        let caseType = this.getCaseType();
        if (caseType === 'CVD' || caseType === 'CVS') {
            return this.caseEventCodeFound('ELME');  // these are the only valid case types where we show this field
        }
        return '-';
    }
    getEligibleForCustodyVisitationMediation(): string {
        let caseType = this.getCaseType();
        if (caseType === 'CVD' || caseType === 'CVS') {
            return this.caseEventCodeFound('ELCU');  // these are the only valid case types where we show this field
        }
        return '-';
    }
    getEligibleForArbitration(): string {
        let caseType = this.getCaseType();
        if (caseType === 'CVD' || caseType === 'CVS') {
            return this.caseEventCodeFound('ELAR');  // these are the only valid case types where we show this field
        }
        return '-';
    }
    getInterpreterNeeded(): string {
        return SafeJSON.getWithDefault('interpreterLanguage', this.json);
    }
    getInterpreterUsed(): string {
        if (this.getInterpreterNeeded() === '-') {
            return '-';
        }
        let used = SafeJSON.getWithDefault('interpreterUsed', this.json);
        if (used === '1') {
            return 'YES';
        }
        return 'NO';
    }
    getCourtSessionDate(): string {
        return SafeJSON.get('caseCalendar.beginDate', this.json);
    }
    getCourtSessionTime(): string {
        return SafeJSON.getWithDefault('caseCalendar.trialTime', this.json);
    }
    getCourtSessionRoomCode(): string {
        return SafeJSON.getWithDefault('caseCalendar.caseCalendarLocation.code', this.json);
    }
    getCourtSessionRoomDescription(): string {
        return SafeJSON.getWithDefault('caseCalendar.caseCalendarLocation.description', this.json);
    }
    getCourtSessionNotes(): string {
        return SafeJSON.getWithDefault('caseCalendar.note', this.json);
    }
    getDigitalScanMicrofilmLocationNumbers(key: string): string {
        let year = SafeJSON.get('json.filmYear', key);
        let stringYear: string = year.toString();
        if (stringYear !== undefined && stringYear !== '') {
            stringYear = stringYear.slice(2, 4);
        }
        stringYear = this.padZeros(stringYear, 2);

        let reel = SafeJSON.get('json.filmReel', key);
        reel = this.padZeros(reel, 3);
        let sequence  = SafeJSON.get('json.filmFrame', key);
        sequence = this.padZeros(sequence, 4);
        return ' ' + stringYear + ' ' + reel + ' ' + sequence;
    }
    padZeros(num, size) {
        let s = num + '';
        while (s.length < size) {
             s = '0' + s;
        }
        return s;
    }
    // Transfer and Appeals /////////////////////////////////////////////////////////////
    getClockDateByCaseEventCode(code: string): string {
        // look in the eventList for the code and if found, return that clock date.
        let eventList: Object[] = this.json.caseEventList;

        if (eventList === undefined) {
            return '';
        }

        for (let i = 0; i < eventList.length; i++) {
            let eventCode = SafeJSON.get('caseEventList[' + i + '].caseEvent.code', this.json);
            if (eventCode === code) {
                return SafeJSON.get('caseEventList[' + i + '].clockDate', this.json);
            }
        }
        return '';
    }
    getInitiatedAppealFromCaseNumber(): string {
        // look in the eventList for INAP code and if found, return that filenumber
        let eventList: Object[] = this.json.caseEventList;

        if (eventList === undefined) {
            return '';
        }

        for (let i = 0; i < eventList.length; i++) {
            let eventCode = SafeJSON.get('caseEventList[' + i + '].caseEvent.code', this.json);
            if (eventCode === 'INAP') {
                return SafeJSON.get('caseEventList[' + i + '].fromCaseNumber.fileNumber', this.json);
            }
        }
        return '-';
    }
    getInitiatedAppealedToCase(): string {
        return this.getCaseNumber();
    }
    getInitiatedAppealedByParty(): string {
        return '*' + SafeJSON.getWithDefault('', this.json);
    }
    getInitiatedAppealedIssue(): string {
        return '*' + SafeJSON.getWithDefault('', this.json);
    }
    getInitiatedResultReturnedByCourt(): string {
        return '*' + SafeJSON.getWithDefault('', this.json);
    }
    getNoticeOfTransferAppealClockDate(appeal: any): string {
        return SafeJSON.get('clockDate', appeal.json);
    }
    getTransferedTo(): string {
        return SafeJSON.getWithDefault('transferedToCase', this.json);
    }
    getTransferedToCaseDate(): string {
        const xferDate = SafeJSON.getWithDefault('transferedToCase.transferedOnDate', this.json);
        return xferDate;
    }
    getTransferedFrom(): string {
        return SafeJSON.getWithDefault('transferedFromCase', this.json);
    }
    getTransferedFromCaseDate(): string {
        const xferDate = SafeJSON.getWithDefault('transferedFromCase.transferedOnDate', this.json);
        return xferDate;
    }
    getTransferedFromCaseNumber(): string {
        let caseNumber = SafeJSON.getWithDefault('transferedFromCase.caseNumber.fileNumber', this.json);
        if (!caseNumber) {
            return caseNumber;
        }
        return FileNumberUtil.stripMiddleZeros(caseNumber);
    }
    getTransferedToCaseNumber(): string {
        let caseNumber = SafeJSON.getWithDefault('transferedToCase.caseNumber.fileNumber', this.json);
        if (!caseNumber) {
            return caseNumber
        }
        return FileNumberUtil.stripMiddleZeros(caseNumber);
    }
    getFromSmallClaimsCourtCaseNumber(): string {
        let fileNumber = SafeJSON.getWithDefault('transferedFromCase.caseNumber.fileNumber', this.json);
        if (fileNumber.indexOf('CVM') === -1) {
            return '-';
        }
        return FileNumberUtil.stripMiddleZeros(fileNumber);
    }
    getToDistrictCourtCaseNumber(): string {
        let fileNumber = SafeJSON.getWithDefault('transferedFromCase.caseNumber.fileNumber', this.json);
        if (fileNumber.indexOf('CVD') === -1) {
            return '-';
        }
        return FileNumberUtil.stripMiddleZeros(fileNumber);
    }
    caseEventCodeFound(field: string): string {
        let eventList: Object[] = this.json.caseEventList;

        if (eventList === undefined) {
            return '-';
        }

        // scan the event list and look for the requested code
        for (let i = 0; i < eventList.length; i++) {
            let code: string = SafeJSON.get('caseEventList[' + i + '].caseEvent.code', this.json);
            if (code === field) {
                return 'YES';
            }
        }
        return 'NO';
    }
    getTransferEventList(): CaseEventHistory[] {
        let code1 = 'INTR';
        let code2 = 'DISP';
        let tlist: CaseEventHistory[] = Array.from(this.caseEventHistoryMap.values())
            .filter(transfer => transfer.getCode() === code1 || transfer.getCode() === code2);
        return tlist;
    }
    getAppealToFormattedShortFileNumber(): string {
        return SafeJSON.get('appealTo.formattedShortFileNumber', this.json);
    }


    /**
    * get the pleadings/issues display list used in search result screen
    * for the pleadings/issues grid table
    */
   getPleadingsIssuesDisplayList(): any[] {
    let pleadingDisplayList: any[] = [];
    let currentCase: CivilCase = this ;

    if (currentCase.getPleadingList !== undefined ) {
      for (let pleading of currentCase.getPleadingList() )  {
        let pleadingClockDate: any = pleading.getClockDate();
        let pleadingType: any = pleading.getType();
        let pleadingIssueType: any = '';
        for (let issue of currentCase.getIssueListByPleadingKey(pleading.getPleadingKey())) {
           pleadingIssueType = issue. getTypeReplaceOther();  // FBR 540

          let pleadingDisplayRow: any = {
             pleadingClockDate: pleadingClockDate,
             pleadingType: pleadingType,
             pleadingIssueType: pleadingIssueType,
          };

          pleadingDisplayList.push(pleadingDisplayRow);
        }
      }
    }

   // sorting in clock date, pleading type and issue type
   pleadingDisplayList.sort( function(p1, p2) {
      if (p1.pleadingClockDate.localeCompare(p2.pleadingClockDate) === 0) {
        if (p1.pleadingType.localeCompare(p2.pleadingType)  === 0 ) {
          return p1.pleadingIssueType.localeCompare(p2.pleadingIssueType);
        } else {
         return p1.pleadingType.localeCompare(p2.pleadingType);
        }
      } else {
        return p1.pleadingClockDate.localeCompare(p2.pleadingClockDate);
      }
    });

    return pleadingDisplayList;
  }
  getLastUpdatedDate(): string {
      let dateTS = this.searchRecordLastUpdateDate;
      let date = this.convertDate(dateTS);
      return date;
  }
  getLastUpdatedTime(): string {
    let dateTS = this.searchRecordLastUpdateDate;
    let time = this.convertTime(dateTS);
    return time;
  }
  getPayoffHoverDisclaimer(): string {
    return  'Payoff amounts were calculated on ' + this.getInterestedCalculatedDate() +
            ' based on the information retrieved from the system of record on ' +
            this.getLastUpdatedDate() + ' at  ' + this.getLastUpdatedTime() +
            '. For the most accurate payoff amount, please see the clerk.';
  }
  getInterestedCalculatedDate(): string {
     let dateTS = this.searchRecordLastUpdateDate;
     let date = this.convertDate(dateTS);
     return date;
  }
  convertDate(date: string): string {
     let datePipe = new DatePipe('en-US');
     return datePipe.transform(date, 'MM/dd/yyyy');
  }
  convertTime(time: string): string {
    let datePipe = new DatePipe('en-US');
    return datePipe.transform(time, 'shortTime');
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/civil/civil-case.ts